/**
 * autismDebugLog.js
 *
 * TEMPORARY, centralised debug logging for the Adult Autism proformas (History /
 * CI, SSI, Observation, Diagnostic Decision and Post-Assessment Discussion).
 *
 * WHY THIS EXISTS
 * While we iterate on the Autism proformas before production we want rich,
 * consistent, easy-to-find traces of the things that most often go wrong:
 *   - what gets persisted to each assessment stage (and the progress/status),
 *   - when a stage is marked complete,
 *   - when AI generation is triggered (and for which stage/section),
 *   - how the Met / Not-met outcome branches the PAD flow.
 *
 * HOW TO USE
 *   import { asdLog } from '../../../utils/autismDebugLog';
 *   asdLog('PAD', 'complete', { patientId, outcome });
 *
 * Every line is prefixed with `[ASD:<scope>]` so the whole feature's logs can be
 * filtered in the browser console with the search term `[ASD:` and removed later
 * by deleting this file plus its call sites (grep for `asdLog(`).
 *
 * TURNING IT OFF
 * Flip ASD_DEBUG to false (or remove the file and its imports) to silence
 * everything in one place — no other code changes required. It is intentionally
 * a plain constant rather than an env var so it is trivial to toggle by hand and
 * impossible to forget in a build pipeline.
 *
 * NOTE (privacy): these logs can include clinical free-text (e.g. goals/notes)
 * to aid debugging. They are console-only (visible to the signed-in clinician's
 * own browser) and MUST be removed before production — see the project rule that
 * this is a clinical tool.
 */

/** Master switch for all Autism proforma debug logging. */
export const ASD_DEBUG = true;

/**
 * Log a single tagged debug line.
 *
 * @param {string} scope - short module tag, e.g. 'PAD' | 'DD' | 'SSI' | 'OBS' |
 *                          'History' | 'ctx' | 'gen'
 * @param {string} event - a short human-readable event name, e.g. 'persist'
 * @param {*} [data]      - optional structured payload (object/array/primitive)
 */
export function asdLog(scope, event, data) {
  if (!ASD_DEBUG) return;
  const tag = `[ASD:${scope}] ${event}`;
  if (data !== undefined) {
    // eslint-disable-next-line no-console
    console.log(tag, data);
  } else {
    // eslint-disable-next-line no-console
    console.log(tag);
  }
}

/**
 * Convenience: summarise a structured-notes array (StructuredNotesEditor value)
 * as `{ count, texts }` so logs stay readable instead of dumping full objects.
 *
 * @param {Array|undefined} notes
 * @returns {{count: number, texts: string[]}}
 */
export function summariseNotes(notes) {
  if (!Array.isArray(notes)) return { count: 0, texts: [] };
  const texts = notes
    .map((n) => (n && typeof n.text === 'string' ? n.text : ''))
    .filter(Boolean);
  return { count: texts.length, texts };
}

export default asdLog;
